/**
 * The operator's notification sound: which one, how loud, and whether at all.
 *
 * <p>Two different things used to make noise — the bell in the dashboard layout and the incoming
 * chime in the open chat — each with its own hardcoded file and, in the chat's case, a volume of 0.5
 * nobody could change. An operator sitting under it for an eight-hour shift had no say in either.
 *
 * <p><b>Stored per device, in localStorage.</b> Deliberately not on the server: how loud a
 * notification should be depends on the machine it is coming out of, and this operator works from
 * both a laptop and a phone. A single synced volume would be wrong on one of them every time. The
 * chosen sound travels with the device for the same reason — a tone that carries over an office is
 * not the one you want on a handset at night.
 */

const KEY_ENABLED = "npe.sound.enabled";
const KEY_NAME = "npe.sound.name";
const KEY_VOLUME = "npe.sound.volume";

/**
 * The catalogue.
 *
 * <p>`ring` is the default, and it is there because of a measurement. The old default
 * (`classic`, now "ملایم قدیمی") is a 3.4-second soft pad averaging -21 dBFS, and it shipped at
 * volume 0.5 — an effective -27 dBFS. No volume slider can make a quiet recording loud; operators
 * in a live office simply did not hear it. `ring` is a dense two-tone phone ring mastered to
 * -0.5 dBFS peak / -9.6 dBFS average: about 17 dB — three to four times — louder than what they
 * heard before. The other files were peak-normalised to -1 dBFS at the same time (+5 dB each).
 *
 * <p>An operator who explicitly picked a sound keeps it — the preference is stored per device and
 * only the *default* changed. `classic` stays in the list for anyone who wants it back.
 */
export const SOUNDS = [
    {id: "ring", label: "زنگ بلند (پیش‌فرض)", file: "/sound-ring.wav"},
    {id: "alert", label: "هشدار", file: "/sound-alert.wav"},
    {id: "ding", label: "زنگ", file: "/sound-ding.wav"},
    {id: "chime", label: "دو نتی", file: "/sound-chime.wav"},
    {id: "marimba", label: "چوبی", file: "/sound-marimba.wav"},
    {id: "soft", label: "ملایم", file: "/sound-soft.wav"},
    {id: "classic", label: "ملایم قدیمی", file: "/notification.wav"},
];

export const DEFAULT_SOUND = "ring";
// Full volume. Half volume was the other half of "nobody hears it"; an operator who finds it too
// loud has a slider, an operator who never hears a customer has a complaint.
export const DEFAULT_VOLUME = 1.0;

const cache = new Map();

function safeGet(key) {
    try {
        return window.localStorage.getItem(key);
    } catch {
        // Private browsing, or storage disabled by policy. Fall back to defaults rather than
        // throwing — a preference that cannot be saved must not stop the sound working.
        return null;
    }
}

function safeSet(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* nothing to do; the setting simply will not persist on this device */
    }
}

const KEY_MIGRATED = "npe.sound.migrated.ring";

/**
 * One-time move of stored defaults to the loud ones.
 *
 * <p>The settings screen writes all three keys whenever ANY of them changes, so every operator who
 * ever touched the volume slider also has name=classic and volume persisted — the old defaults,
 * stored as if chosen. Nobody chose a 3.4-second whisper; it was simply what was there. Move those
 * two values to the new defaults once, and let the slider be moved again by anyone who wants it
 * quieter. An operator who explicitly picked a DIFFERENT sound keeps it.
 */
function migrateStoredDefaults() {
    if (safeGet(KEY_MIGRATED) === "1") return;
    if (safeGet(KEY_NAME) === "classic") safeSet(KEY_NAME, DEFAULT_SOUND);
    const v = parseFloat(safeGet(KEY_VOLUME));
    if (Number.isFinite(v) && v < DEFAULT_VOLUME) safeSet(KEY_VOLUME, String(DEFAULT_VOLUME));
    safeSet(KEY_MIGRATED, "1");
}

export function getSoundPreferences() {
    migrateStoredDefaults();
    const name = safeGet(KEY_NAME);
    const rawVolume = parseFloat(safeGet(KEY_VOLUME));
    return {
        // Enabled unless explicitly turned off, so an operator who has never opened the setting
        // still hears their messages.
        enabled: safeGet(KEY_ENABLED) !== "false",
        name: SOUNDS.some((s) => s.id === name) ? name : DEFAULT_SOUND,
        volume: Number.isFinite(rawVolume) ? Math.min(1, Math.max(0, rawVolume)) : DEFAULT_VOLUME,
    };
}

export function setSoundPreferences({enabled, name, volume}) {
    if (enabled !== undefined) safeSet(KEY_ENABLED, enabled ? "true" : "false");
    if (name !== undefined && SOUNDS.some((s) => s.id === name)) safeSet(KEY_NAME, name);
    if (volume !== undefined) safeSet(KEY_VOLUME, String(Math.min(1, Math.max(0, volume))));
}

function fileFor(id) {
    return (SOUNDS.find((s) => s.id === id) || SOUNDS[0]).file;
}

/** One Audio element per file, reused — creating one per notification leaks on a busy shift. */
function audioFor(id) {
    const file = fileFor(id);
    if (!cache.has(file)) {
        cache.set(file, new Audio(file));
    }
    return cache.get(file);
}

/**
 * Play the operator's chosen notification sound.
 *
 * <p>Never throws and never rejects visibly. Sound is a courtesy: a blocked autoplay, a missing file
 * or a device with no audio must not surface as an error on a screen somebody is working in.
 *
 * @param {{repeat?: number}} [opts] `repeat` plays the sound that many times back to back (default 1).
 *        Used when the operator is not looking at the console: a second ring a moment later is what
 *        turns "was that mine?" into getting up and checking.
 */
export function playNotificationSound(opts) {
    try {
        const {enabled, name, volume} = getSoundPreferences();
        if (!enabled || volume <= 0) return;
        const times = Math.max(1, Math.min(3, Math.floor((opts && opts.repeat) || 1)));
        const audio = audioFor(name);
        audio.volume = volume;

        let remaining = times;
        const playOnce = () => {
            remaining -= 1;
            audio.currentTime = 0;
            const p = audio.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        };
        // Chain on `ended` rather than a timer so back-to-back plays never overlap whatever the
        // file's length is. Handler is replaced each call, so an earlier notification's chain
        // cannot keep ringing on top of this one.
        audio.onended = () => {
            if (remaining > 0) setTimeout(playOnce, 350);
        };
        playOnce();
    } catch {
        /* sound is optional */
    }
}

let unlockInstalled = false;

/**
 * Make the sound work from the operator's very first click anywhere on the console.
 *
 * <p>Browsers refuse to play audio until the user has interacted with the document. Until now the
 * unlock ran only when the bell was clicked — and an operator who signs in and just waits never
 * clicks the bell, so their first notification of the shift was silent, and the second, and every
 * one until they happened to touch the bell. One-shot listeners on the first pointer/key event turn
 * that first interaction — whatever it is — into the unlock. Once granted, the browser keeps the
 * permission for the page, including while the tab is in the background.
 */
export function installSoundUnlock() {
    if (unlockInstalled || typeof document === "undefined") return;
    unlockInstalled = true;
    const events = ["pointerdown", "keydown", "touchstart"];
    const onFirst = () => {
        events.forEach((e) => document.removeEventListener(e, onFirst, true));
        unlockNotificationSound();
    };
    events.forEach((e) => document.addEventListener(e, onFirst, true));
}

/** Play a specific sound at a specific volume, for the preview button in settings. */
export function previewSound(id, volume) {
    try {
        const audio = audioFor(id);
        audio.volume = Math.min(1, Math.max(0, volume ?? DEFAULT_VOLUME));
        audio.currentTime = 0;
        const p = audio.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
        /* preview is best-effort */
    }
}

/**
 * Satisfy the browser's autoplay policy while the operator is interacting with the page.
 *
 * <p>Browsers refuse to play audio until the user has interacted with the document, and the first
 * notification of a shift usually arrives before any click. Playing muted and immediately pausing
 * during a real click marks the element as user-activated, so the sound works when it matters.
 */
export function unlockNotificationSound() {
    try {
        const {name} = getSoundPreferences();
        const audio = audioFor(name);
        audio.muted = true;
        const p = audio.play();
        if (p && typeof p.then === "function") {
            p.then(() => {
                audio.pause();
                audio.currentTime = 0;
                audio.muted = false;
            }).catch(() => {
                audio.muted = false;
            });
        } else {
            audio.muted = false;
        }
    } catch {
        /* nothing to unlock */
    }
}
